import React from "react";
import { Link } from "react-router-dom";
import { FiExternalLink } from "react-icons/fi";
import { useInView } from "react-intersection-observer";
import {
  typography,
  spacing,
  components,
  gradients,
} from "../styles/designSystem";
import { IN_VIEW_OPTIONS, reveal } from "../styles/animations";
import PageHeader from "../components/PageHeader";
import { projects } from "../data/projectsData";
import { CTA } from "../sections/CTA";

// ─── Sub-components ──────────────────────────────────────────────────────────

const ProjectVisual = ({ project }) => (
  <div className="relative flex min-h-[400px] items-center md:min-h-[480px] lg:min-h-[560px]">
    {/* Decorative shapes */}
    <div className="animate-float pointer-events-none absolute -left-4 top-2 hidden h-20 w-20 rounded-[1.75rem] bg-accent/20 md:block" />
    <div className="pointer-events-none absolute -right-6 -top-5 h-28 w-28 rounded-full border-[10px] border-primary/10" />
    <div className="pointer-events-none absolute bottom-8 left-8 h-20 w-20 rotate-12 rounded-[1.5rem] border border-accent/30 bg-accent/[0.08]" />
    <div className="absolute -inset-5 rounded-[3rem] bg-primary/[0.09] blur-3xl transition duration-700 group-hover:scale-105" />

    {/* Main browser mockup */}
    <div
      className={`relative w-full rounded-[1.75rem] border border-gray-200/70 p-3 shadow-2xl transition-all duration-500 group-hover:-translate-y-1 group-hover:scale-[1.015] ${gradients.subtle}`}
    >
      <div className="overflow-hidden rounded-[1.25rem] bg-white">
        <div className="flex items-center gap-2 border-b border-gray-200/70 bg-gray-50 px-4 py-3">
          <span className="h-3 w-3 rounded-full bg-red-400" />
          <span className="h-3 w-3 rounded-full bg-yellow-400" />
          <span className="h-3 w-3 rounded-full bg-green-400" />
          <span className="ml-3 truncate text-xs text-gray-500">
            {project.link.replace(/^https?:\/\//, "")}
          </span>
        </div>

        <img
          src={project.image}
          alt={`${project.title} website preview`}
          className="h-[340px] w-full object-cover object-top sm:h-[420px] lg:h-[500px]"
        />
      </div>
    </div>

    {/* Small second screenshot for depth */}
    {project.detailImage && (
      <div className="absolute -bottom-8 -left-7 hidden w-[42%] rotate-[-5deg] overflow-hidden rounded-2xl border border-white/80 bg-white p-2 shadow-2xl transition-transform duration-500 group-hover:-translate-y-3 group-hover:rotate-[-2deg] md:block">
        <div className="overflow-hidden rounded-xl bg-gray-100">
          <img
            src={project.detailImage}
            alt=""
            aria-hidden="true"
            className="h-36 w-full object-cover object-top"
          />
        </div>
      </div>
    )}
  </div>
);

const ProjectCard = ({ project, index }) => {
  const visualFirst = index % 2 === 1;

  return (
    <article
      className={`${components.card.base} group relative overflow-hidden rounded-[2.25rem] border border-secondary/10 bg-white/85 shadow-xl backdrop-blur-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl`}
    >
      {/* Decorative layers */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/[0.07] via-transparent to-accent/[0.09] opacity-70" />
      <div className="pointer-events-none absolute -left-20 -top-20 h-56 w-56 rounded-full bg-primary/[0.09] blur-3xl transition-transform duration-700 group-hover:scale-125" />
      <div className="pointer-events-none absolute -bottom-24 right-[30%] h-56 w-56 rounded-full bg-accent/[0.09] blur-3xl transition-transform duration-700 group-hover:scale-125" />
      <div className="pointer-events-none absolute right-10 top-10 h-16 w-16 rotate-12 rounded-[1.6rem] border border-primary/20 bg-primary/[0.04]" />
      <div className="pointer-events-none absolute bottom-12 left-[46%] h-3 w-3 rounded-full bg-primary/50" />

      <div className="relative grid gap-12 p-7 md:p-10 lg:grid-cols-[0.9fr,1.1fr] lg:items-center lg:gap-16 lg:p-14 xl:p-16">
        {/* Content */}
        <div
          className={`flex flex-col justify-between gap-9 ${
            visualFirst ? "lg:order-2" : "lg:order-1"
          }`}
        >
          <div className="space-y-5">
            <p className={`${typography.label} text-primary`}>
              {project.category}
            </p>

            <h2
              className={`${typography.heading.h2} text-secondary transition-colors duration-300 group-hover:text-primary`}
            >
              {project.title}
            </h2>

            <p className={`${typography.body.base} max-w-xl`}>
              {project.description}
            </p>
          </div>

          {/* Features list */}
          <div className="relative rounded-3xl border border-secondary/10 bg-white/60 p-6 shadow-sm">
            <div className="absolute -left-2 top-8 h-10 w-1 rounded-full bg-primary" />

            <div className="space-y-3">
              {project.features.map((feature) => (
                <div
                  key={feature}
                  className="group/feature flex items-start gap-3"
                >
                  <span className="mt-2.5 h-2 w-2 shrink-0 rounded-full bg-primary transition-all duration-300 group-hover/feature:scale-125 group-hover/feature:bg-secondary" />
                  <p
                    className={`${typography.body.small} text-gray-700 transition-colors group-hover/feature:text-secondary`}
                  >
                    {feature}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <a
              href={project.link}
              target="_blank"
              rel="noreferrer"
              className={components.button.primary}
            >
              Visit website
              <FiExternalLink />
            </a>

            <Link
              to={`/projects/${project.id}`}
              className={`${components.button.secondary} px-8 py-4`}
            >
              Learn more
            </Link>
          </div>
        </div>

        {/* Visual showcase */}
        <div className={visualFirst ? "lg:order-1" : "lg:order-2"}>
          <ProjectVisual project={project} />
        </div>
      </div>
    </article>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const ProjectsPage = () => {
  const { ref: headerRef, inView: headerInView } = useInView(IN_VIEW_OPTIONS);
  const { ref: projectsRef, inView: projectsInView } =
    useInView(IN_VIEW_OPTIONS);
  const { ref: ctaRef, inView: ctaInView } = useInView(IN_VIEW_OPTIONS);

  return (
    <main className="relative min-h-screen overflow-hidden bg-white pt-24 md:pt-32">
      <section
        className={`${spacing.container} ${spacing.section} relative z-10`}
      >
        <PageHeader
          sectionRef={headerRef}
          visible={headerInView}
          label="Portfolio"
          title={
            <>
              Clients that{" "}
              <span className="font-semibold text-primary">trust us</span>
            </>
          }
          subtitle="Take a closer look at the work we did for some of our clients. Each project reflects our commitment to clear design, technical excellence, and real business results."
        />

        {/* One full-width project per row */}
        <div
          ref={projectsRef}
          className={`${reveal(projectsInView)} space-y-20 md:space-y-28 lg:space-y-32`}
        >
          {projects.map((project, index) => (
            <ProjectCard key={project.id} project={project} index={index} />
          ))}
        </div>

        <CTA
          sectionRef={ctaRef}
          visible={ctaInView}
          title="Ready to work together?"
          subtitle="Let's discuss your project and see if we're a good fit for your goals."
        />
      </section>
    </main>
  );
};

export default ProjectsPage;
